/* eslint-disable replo/no-tailwind-literal-color */

import * as React from "react";

import { Button } from "@replo/design-system/components/button/Button";
import { Separator } from "@replo/design-system/components/separator/Separator";
import twMerge from "@replo/design-system/utils/twMerge";
import Blockquote from "@tiptap/extension-blockquote";
import BulletList from "@tiptap/extension-bullet-list";
import Link from "@tiptap/extension-link";
import ListItem from "@tiptap/extension-list-item";
import OrderedList from "@tiptap/extension-ordered-list";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { MinimalTipTapLinkEditorModal } from "@v2/features/products/modals/MinimalTipTapLinkEditorModal";
import {
  Bold,
  Code,
  Heading1,
  Heading2,
  Heading3,
  Italic,
  Link as LinkIcon,
  List,
  ListOrdered,
  Minus,
  Quote,
  Redo,
  Strikethrough,
  Undo,
} from "lucide-react";

interface MinimalTiptapProps {
  content?: string;
  onChange?: (content: string) => void;
  placeholder?: string;
  editable?: boolean;
  className?: string;
}
const MinimalTiptap: React.FC<MinimalTiptapProps> = ({
  content = "",
  onChange,
  placeholder = "Start typing...",
  editable = true,
  className,
}) => {
  const [linkEditorData, setLinkEditorData] = React.useState<{
    linkUrl: string;
    linkText: string;
    onSave: (url: string, text: string) => void;
  } | null>(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        bulletList: false,
        orderedList: false,
        listItem: false,
        blockquote: false,
        heading: {
          levels: [1, 2, 3, 4, 5, 6],
          HTMLAttributes: {
            class: "font-bold",
          },
        },
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: "text-blue-600 underline hover:text-blue-800",
        },
      }),
      Blockquote.configure({
        HTMLAttributes: {
          class: "border-l-4 border-gray-300 pl-2 italic",
        },
      }),
      BulletList.configure({
        keepMarks: true,
        keepAttributes: false,
        itemTypeName: "listItem",
      }),
      OrderedList.configure({
        keepMarks: true,
        keepAttributes: false,
        itemTypeName: "listItem",
      }),
      ListItem,
    ],
    content,
    editable,
    onUpdate: ({ editor }) => {
      onChange?.(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: twMerge(
          "prose prose-sm sm:prose-base lg:prose-lg xl:prose-2xl focus:outline-none",
          "min-h-[70.5px] p-4 border-0",
          "prose-ul:list-disc prose-ol:list-decimal prose-li:ml-4",
          "[&_ol]:list-decimal [&_ul]:list-disc [&_li]:ml-4",
          "prose-blockquote:border-l-2 prose-blockquote:border-gray-300 prose-blockquote:pl-2 prose-blockquote:italic",
          "prose-headings:font-bold [&_h1]:text-xl [&_h2]:text-lg [&_h3]:text-base",
        ),
      },
    },
  });

  const openLinkModal = React.useCallback(() => {
    if (!editor) {
      return;
    }
    const previousUrl = editor.getAttributes("link").href;
    const selectedText = editor.state.doc.textBetween(
      editor.state.selection.from,
      editor.state.selection.to,
    );

    setLinkEditorData({
      linkUrl: previousUrl ?? "",
      linkText: selectedText ?? "",
      onSave: (url: string, text: string) => {
        if (url === "") {
          editor.chain().focus().extendMarkRange("link").unsetLink().run();
        } else {
          const displayText = text ?? url;

          if (editor.state.selection.from !== editor.state.selection.to) {
            editor
              .chain()
              .focus()
              .deleteSelection()
              .insertContent(displayText)
              .setTextSelection({
                from: editor.state.selection.from,
                to: editor.state.selection.from + displayText.length,
              })
              .setLink({ href: url })
              .run();
          } else {
            const currentPos = editor.state.selection.from;
            editor
              .chain()
              .focus()
              .insertContent(displayText)
              .setTextSelection({
                from: currentPos,
                to: currentPos + displayText.length,
              })
              .setLink({ href: url })
              .run();
          }
        }
      },
    });
  }, [editor]);

  if (!editor) {
    return null;
  }
  return (
    <div
      className={twMerge(
        "border-0.5 border-component-border rounded-lg overflow-hidden bg-sheet",
        className,
      )}
    >
      <div className="border-b-0.5 border-component-border p-1 flex flex-wrap items-center gap-1">
        <Button
          icon
          variant="tertiary"
          size="sm"
          UNSAFE_className={twMerge(editor.isActive("bold") && "bg-surface")}
          onClick={() => editor.chain().focus().toggleBold().run()}
          disabled={!editor.can().chain().focus().toggleBold().run()}
          aria-label="Toggle bold"
        >
          <Bold className="text-muted" />
        </Button>

        <Button
          icon
          variant="tertiary"
          size="sm"
          UNSAFE_className={twMerge(editor.isActive("italic") && "bg-surface")}
          onClick={() => editor.chain().focus().toggleItalic().run()}
          disabled={!editor.can().chain().focus().toggleItalic().run()}
          aria-label="Toggle italic"
        >
          <Italic className="text-muted" />
        </Button>

        <Button
          icon
          variant="tertiary"
          size="sm"
          UNSAFE_className={twMerge(editor.isActive("strike") && "bg-surface")}
          onClick={() => editor.chain().focus().toggleStrike().run()}
          disabled={!editor.can().chain().focus().toggleStrike().run()}
          aria-label="Toggle strikethrough"
        >
          <Strikethrough className="text-muted" />
        </Button>

        <Button
          icon
          variant="tertiary"
          size="sm"
          UNSAFE_className={twMerge(editor.isActive("code") && "bg-surface")}
          onClick={() => editor.chain().focus().toggleCode().run()}
          disabled={!editor.can().chain().focus().toggleCode().run()}
          aria-label="Toggle code"
        >
          <Code className="text-muted" />
        </Button>

        <Button
          icon
          variant="tertiary"
          size="sm"
          UNSAFE_className={twMerge(editor.isActive("link") && "bg-surface")}
          onClick={openLinkModal}
          aria-label="Set link"
        >
          <LinkIcon className="text-muted" />
        </Button>
        <Separator orientation="vertical" className="h-6" />
        <Button
          icon
          variant="tertiary"
          size="sm"
          UNSAFE_className={twMerge(
            editor.isActive("heading", { level: 1 }) && "bg-surface",
          )}
          onClick={() => {
            return editor.chain().focus().toggleHeading({ level: 1 }).run();
          }}
          disabled={
            !editor.can().chain().focus().toggleHeading({ level: 1 }).run()
          }
          aria-label="Toggle heading 1"
        >
          <Heading1 className="text-muted" />
        </Button>

        <Button
          icon
          variant="tertiary"
          size="sm"
          UNSAFE_className={twMerge(
            editor.isActive("heading", { level: 2 }) && "bg-surface",
          )}
          onClick={() => {
            return editor.chain().focus().toggleHeading({ level: 2 }).run();
          }}
          disabled={
            !editor.can().chain().focus().toggleHeading({ level: 2 }).run()
          }
          aria-label="Toggle heading 2"
        >
          <Heading2 className="text-muted" />
        </Button>

        <Button
          icon
          variant="tertiary"
          size="sm"
          UNSAFE_className={twMerge(
            editor.isActive("heading", { level: 3 }) && "bg-surface",
          )}
          onClick={() => {
            return editor.chain().focus().toggleHeading({ level: 3 }).run();
          }}
          disabled={
            !editor.can().chain().focus().toggleHeading({ level: 3 }).run()
          }
          aria-label="Toggle heading 3"
        >
          <Heading3 className="text-muted" />
        </Button>
        <Separator orientation="vertical" className="h-6" />
        <Button
          icon
          variant="tertiary"
          size="sm"
          UNSAFE_className={twMerge(
            editor.isActive("bulletList") && "bg-surface",
          )}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          disabled={!editor.can().chain().focus().toggleBulletList().run()}
          aria-label="Toggle bullet list"
        >
          <List className="text-muted" />
        </Button>

        <Button
          icon
          variant="tertiary"
          size="sm"
          UNSAFE_className={twMerge(
            editor.isActive("orderedList") && "bg-surface",
          )}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          disabled={!editor.can().chain().focus().toggleOrderedList().run()}
          aria-label="Toggle ordered list"
        >
          <ListOrdered className="text-muted" />
        </Button>

        <Button
          icon
          variant="tertiary"
          size="sm"
          UNSAFE_className={twMerge(
            editor.isActive("blockquote") && "bg-surface",
          )}
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          disabled={!editor.can().chain().focus().toggleBlockquote().run()}
          aria-label="Toggle blockquote"
        >
          <Quote className="text-muted" />
        </Button>
        <Separator orientation="vertical" className="h-6" />
        <Button
          icon
          variant="tertiary"
          size="sm"
          onClick={() => editor.chain().focus().setHorizontalRule().run()}
          aria-label="Insert horizontal rule"
        >
          <Minus className="text-muted" />
        </Button>
        <Separator orientation="vertical" className="h-6" />
        <Button
          icon
          variant="tertiary"
          size="sm"
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().chain().focus().undo().run()}
          aria-label="Undo"
        >
          <Undo className="text-muted" />
        </Button>

        <Button
          icon
          variant="tertiary"
          size="sm"
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().chain().focus().redo().run()}
          aria-label="Redo"
        >
          <Redo className="text-muted" />
        </Button>
      </div>

      <div className="overflow-y-auto max-h-[210px]">
        <EditorContent editor={editor} placeholder={placeholder} />
      </div>
      {linkEditorData && (
        <MinimalTipTapLinkEditorModal
          open
          onOpenChange={(open) => {
            if (!open) {
              setLinkEditorData(null);
            }
          }}
          linkUrl={linkEditorData.linkUrl}
          linkText={linkEditorData.linkText}
          onSave={linkEditorData.onSave}
        />
      )}
    </div>
  );
};
export { MinimalTiptap };
